import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../types/translation/TranslationKey';
import { t } from '../../../../types/translation/Translator';
import Table from '../../../components/Table';
import { AssetContext } from '../../../../context/AssetContext';
import { Asset } from '../../../../types/asset/asset';
import { AssetFilter } from '../../../../VentoryUI/components/filters/AssetFilter/AssetFilter';
import { AssetTable } from '../../Common/AssetTable';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { CompanyContext } from '../../../../context/CompanyContext';
import { useNavigate } from 'react-router-dom';
import { UnitOfMeasure } from '../../../../types/uom';
import { UserContext } from '../../../../context/UserContext';
import { AssetTransactionContext } from '../../../../context/AssetTransactionContext';
import {
  AssetTransactionParentType,
  AssetTransactionStatus,
} from '../../../../types/asset_transaction/assetTransaction';
import { AssetTransactionUtil } from '../../../../types/asset_transaction/assetTransaction.util';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { Routing } from '../../../../util/routes.util';
import { TagRelationContext } from '../../../../context/TagRelationContext';
import { TableSettingsContext } from '../../../../context/TableSettingsContext';
import { EntityType } from '../../../../types/comment';
import AssetImportModal from '../Modals/AssetImportModal';
import CreateAssetModal from '../Modals/CreateAssetModal';
import AssetExportModal from '../Modals/AssetExportModal';
import SearchBarWithFilter from '../../../../VentoryUI/components/common/SearchBarWithFilter';
import {
  AssetTableSettings,
  AssetTableSettingsColumn,
} from '../../../../types/table_settings/asset/AssetTableSettings';
import { CreateButton, ExportIconButton, ImportIconButton } from '../../../components/Button';
import { SettingsIconButton } from '../../../icons';

export default function AssetOverviewPane() {
  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { assets, assetsLoading } = useContext(AssetContext);
  const { assetTransactions, assetTransactionsLoading } = useContext(AssetTransactionContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { getTableSettings } = useContext(TableSettingsContext);
  const { customFields } = useContext(CustomFieldContext);
  const { tagRelations } = useContext(TagRelationContext);

  const assetTableSettings = getTableSettings(EntityType.asset, currentCompany.id);

  const [openImportModal, setOpenImportModal] = useState<boolean>(false);
  const [openExportModal, setOpenExportModal] = useState<boolean>(false);

  const filter = new AssetFilter(
    productMasterData,
    companyUsers,
    assets,
    assetTransactions,
    assetTableSettings,
    customFields,
    tagRelations,
    currentCompany,
  );

  const groupAssets = (): Asset[] => {
    const grouped: Map<string, Asset> = new Map();

    const allAssets = [
      ...[...assets.values()],
      ...AssetTransactionUtil.groupByParent(
        [...assetTransactions.values()].filter(
          transaction =>
            transaction.status === AssetTransactionStatus.pending &&
            transaction.parentType !== AssetTransactionParentType.returnAsset,
        ),
      ).map(transaction => new Asset(transaction)),
    ];

    for (const item of allAssets) {
      const pmd = productMasterData.get(item.product.productMasterDataId);
      if (!pmd) continue;

      const groupedItem = grouped.get(pmd.id) || new Asset(item);
      groupedItem.product.unitQuantity = (
        BigInt((grouped.get(pmd.id)?.product.unitQuantity || BigInt('0')).toString()) +
        BigInt(item.product.unitQuantity)
      ).toString();
      groupedItem.product.quantity = parseFloat(
        UnitOfMeasure.toQuantity(pmd.unitOfMeasure, groupedItem.product.unitQuantity),
      );

      // Reserved quantity
      if (item.location) {
        groupedItem.product.reservedUnitQuantity = (
          BigInt((grouped.get(pmd.id)?.product.reservedUnitQuantity || BigInt('0')).toString()) +
          BigInt(item.product.unitQuantity)
        ).toString();
        groupedItem.product.reservedQuantity = parseFloat(
          UnitOfMeasure.toQuantity(pmd.unitOfMeasure, groupedItem.product.reservedUnitQuantity),
        );
      }

      grouped.set(pmd.id, new Asset(groupedItem));
    }

    return [...grouped.values()];
  };

  const [filteredItems, setFilteredItems] = useState<Asset[]>([...assets.values()]);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);

  const items = useMemo(() => {
    return groupAssets();
  }, [assets, assetTransactions, productMasterData]);

  const columns = [
    {
      index: 0,
      column: AssetTableSettingsColumn.productName,
      label: t(TranslationKey.assetName).singular.label,
    },
    {
      index: 1,
      column: AssetTableSettingsColumn.productNumber,
      label: t(TranslationKey.assetNumber).singular.label,
    },
    {
      index: 2,
      column: AssetTableSettingsColumn.availableQuantity,
      label: t(TranslationKey.quantity).singular.label,
    },
  ];

  const allItems = useMemo(() => {
    const transactions = [...assetTransactions.values()]
      .filter(
        transaction =>
          transaction.status === AssetTransactionStatus.pending &&
          transaction.parentType !== AssetTransactionParentType.returnAsset,
      )
      .map(transaction => new Asset(transaction));
    return [...assets.values(), ...transactions];
  }, [assets, assetTransactions]);

  const pmdIds = new Set(filteredItems.map(item => item.product.productMasterDataId));

  const tableSettings = AssetTableSettings.default(currentCompany.id);
  tableSettings.columns = columns;

  return (
    <>
      {openImportModal && <AssetImportModal open={openImportModal} setOpen={setOpenImportModal} />}
      {openExportModal && <AssetExportModal open={openExportModal} setOpen={setOpenExportModal} />}
      {openCreateModal && <CreateAssetModal open={openCreateModal} setOpen={setOpenCreateModal} />}

      <div className='flex flex-grow flex-col gap-4'>
        <div className='flex justify-between gap-2'>
          <SearchBarWithFilter
            loading={assetsLoading || productMasterDataLoading || assetTransactionsLoading}
            items={allItems}
            setItems={setFilteredItems}
            placeholder={t(TranslationKey.searchForAsset).plural.label}
            filter={filter}
          />
          <div className='flex gap-1'>
            <ExportIconButton onClick={() => setOpenExportModal(true)} />
            <ImportIconButton onClick={() => setOpenImportModal(true)} />
            <CreateButton onClick={() => setOpenCreateModal(true)} />
            <SettingsIconButton onClick={() => navigate(Routing.assets.settings.overview())} />
          </div>
        </div>
        <Table
          loading={assetsLoading || productMasterDataLoading || assetTransactionsLoading}
          items={items.filter(item => pmdIds.has(item.product.productMasterDataId))}
          headers={AssetTable.header(productMasterData, companyUsers, tableSettings, undefined, locale, currentCompany)}
          onClick={item => {
            navigate(Routing.assets.detail(item.product.productMasterDataId).stock());
          }}
          totalItemCount={items.length}
          filter={filter}
        />
      </div>
    </>
  );
}
